import _ from 'lodash';
import { useEffect, useState } from 'react';
import { fetchHolterEvents, checkEventDataChange, formatEventStrip } from '@/entities/event';
import { KEY_CANCEL, KEY_RECOIL } from '@/shared/config';
import { updateStripReview } from '@/entities/beat';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectEventChanges,
  selectHolterEvents,
  selectIsLoadingStrip,
  selectPageIndex,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { HeartRateEventBoxProps } from './heartRateEventBox';

/**
 * Loads and tracks the single holter event shown in the rhythm events tab's heart-rate event box. It fetches events of the active rhythm type for the study and profile whenever that type, the page index or the ECG render flag changes, selects the first result and jumps the viewer to its start, and mirrors the event's reviewed flag from any pending event change.
 *
 * @param activeButton - The rhythm event type currently selected in the tab.
 * @param studyId - Identifier of the study being reported.
 * @param profileId - Identifier of the report profile.
 * @returns An object carrying the loaded holterEvent.
 */
const useHeartRateEventBox = ({ activeButton, studyId, profileId }: HeartRateEventBoxProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the rhythm events tab's list of pending event changes. Passes the TAB_2 key with the new event change array or updater to the store's setEventChanges action.
   *
   * @param v - The next event change array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const [isReviewed, setIsReviewed] = useState(false);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the rhythm events viewer should jump to. Passes the TAB_2 key with the new jump date or updater to the store's setJumpDate action.
   *
   * @param v - The next jump date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets which portion of the jumped-to day the rhythm events viewer scrolls into. Passes the TAB_2 key with the new jump portion or updater to the store's setJumpPortion action.
   *
   * @param v - The next jump portion, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the rhythm events tab's event options used for undo and redo grouping. Passes the TAB_2 key with the new event options or updater to the store's setEventOptions action.
   *
   * @param v - The next event options, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Toggles the strip loading indicator of the rhythm events tab. Passes the TAB_2 key with the new loading flag or updater to the store's setIsLoadingStrip action.
   *
   * @param v - The next loading flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the rhythm events tab's selected strip. Passes the TAB_2 key with the new selected strip or updater to the store's setSelectedStrip action.
   *
   * @param v - The next selected strip, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const setHolterEventsAction = useReportStore((s) => s.actions.setHolterEvents);
  /**
   * Writes the holter events held by the rhythm events tab. Passes the TAB_2 key with the new holter event array or updater to the store's setHolterEvents action.
   *
   * @param v - The next holter event array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setHolterEvents = (v: Parameters<typeof setHolterEventsAction>[1]) =>
    setHolterEventsAction(keyRecoil, v);
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const holterEvent = holterEvents[0];
  let isActiveQuery: boolean;

  /**
   * Toggles the reviewed flag on the selected rhythm event and records it as a pending event change. Recomputes the change list with updateStripReview, and when the result still differs from the server event it keeps that entry, otherwise it removes it; either way the undo and redo event options and the event change list are updated together.
   *
   * @param isReviewed - The reviewed state to apply to the selected event.
   * @returns Nothing.
   */
  const onClickMarkReview = (isReviewed: boolean) => {
    // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing idEvent on some members - atom-type gap.
    const selectedStripAny = selectedStrip as any;
    if (selectedStripAny?.idEvent) {
      const stripChangesUpdate = updateStripReview({
        stripChanges: eventChanges,
        strip: selectedStrip,
        isReviewed,
      });
      const changedEvent = _.find(
        stripChangesUpdate,
        (x) => x.idEvent === selectedStripAny.idEvent,
      );
      //* Remove from event change if user only change review to original
      if (checkEventDataChange(holterEvent, changedEvent)) {
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: stripChangesUpdate,
        }));
        setEventChanges(stripChangesUpdate as any);
      } else {
        _.remove(stripChangesUpdate, (x) => x.idEvent === selectedStripAny.idEvent);
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: stripChangesUpdate,
        }));
        setEventChanges(stripChangesUpdate as any);
      }
    }
  };

  /**
   * Moves the ECG viewer to the start of the given event. Sets the jump date and resets the jump portion to the beginning of the day.
   *
   * @param start - The timestamp to jump the viewer to.
   * @returns Nothing.
   */
  const onClickJumpTo = (start: any) => {
    setJumpDate(start);
    setJumpPortion(0);
  };

  /**
   * Fetches the holter events of the active rhythm type for the current study and profile. Raises the strip loading flag, queries under the holter AI cancel key, formats the returned events, and while the effect is still active stores them, selects the first one, clears the loading flag and jumps the viewer to that event's start; failures are logged.
   *
   * @returns A promise resolving once the fetched events are stored and the viewer has jumped, or the failure has been logged.
   */
  const handleFetchHolterEvents = async () => {
    setIsLoadingStrip(true);
    try {
      const type = activeButton;
      const holterEventsFilter = {
        studyId,
        profileId,
        types: [type],
      };
      const { events } = await fetchHolterEvents(
        holterEventsFilter,
        0,
        true,
        KEY_CANCEL.API_HOLTER_AI,
      );
      const newHolterEvents = formatEventStrip(events);
      if (isActiveQuery) {
        setHolterEvents(newHolterEvents as any);
        setSelectedStrip(newHolterEvents[0]);
        setIsLoadingStrip(false);
        if (newHolterEvents?.[0]?.start) {
          setJumpDate(newHolterEvents?.[0]?.start);
          setJumpPortion(0);
        }
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      handleFetchHolterEvents();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [activeButton, pageIndex, isRenderViewerEcg]);

  useEffect(() => {
    if (holterEvent) {
      const ele = eventChanges.find((x) => x.id === holterEvent.id);
      setIsReviewed(ele ? ele?.isReviewed : holterEvent?.isReviewed);
    }
  }, [eventChanges, holterEvent]);

  return {
    holterEvent,
  };
};

export default useHeartRateEventBox;
